import { OAuthProvider } from '@mondaygpt/constants'

/**
 * Per-platform behavior config for {@link usePlatformPublish}.
 *
 * Each platform's connect/publish flow diverges slightly on which analytics
 * fire and whether to verify the connection after OAuth. Rather than passing
 * boolean escape hatches at every call site, we centralize the divergences
 * here and look them up by `provider`.
 */
export interface PlatformPublishConfig {
  /** Fire SOCIAL_CONNECT_DIALOG_VIEWED when the connect dialog opens. */
  trackDialogViewed: boolean
  /** Fire SOCIAL_CONNECT_DIALOG_DISMISSED when the connect dialog closes. */
  trackDialogDismissed: boolean
  /** Fire INTEGRATION_CONNECT_* analytics during the connect flow. */
  trackConnectAnalytics: boolean
  /** Re-check integration status and retrigger publish after OAuth success. */
  verifyAfterConnect: boolean
}

export const DEFAULT_PLATFORM_PUBLISH_CONFIG: PlatformPublishConfig = {
  trackDialogViewed: true,
  trackDialogDismissed: true,
  trackConnectAnalytics: true,
  verifyAfterConnect: true,
}

/**
 * Per-platform overrides — only list fields that diverge from defaults.
 *
 * - X (twitter): does not track dialog viewed/dismissed.
 * - TikTok: original Dialog used a bare setShow* as onOpenChange, so dismissal
 *   was never tracked.
 * - Facebook: connect flow is a no-op after OAuth (user still needs to pick a
 *   Page), so we skip verify + INTEGRATION_CONNECT_* analytics.
 * - Pinterest, LinkedIn: use defaults.
 */
export const platformPublishConfigs: Partial<
  Record<OAuthProvider, Partial<PlatformPublishConfig>>
> = {
  [OAuthProvider.TWITTER]: {
    trackDialogViewed: false,
    trackDialogDismissed: false,
  },
  [OAuthProvider.TIKTOK]: {
    trackDialogDismissed: false,
  },
  [OAuthProvider.FACEBOOK]: {
    trackConnectAnalytics: false,
    verifyAfterConnect: false,
  },
}

export function resolvePlatformPublishConfig(provider: OAuthProvider): PlatformPublishConfig {
  return { ...DEFAULT_PLATFORM_PUBLISH_CONFIG, ...platformPublishConfigs[provider] }
}
